/**
 * @fileoverview Instructor-facing organization membership cards (Spec 12).
 * Multi-org: renders ONE card per active membership from
 * ``user.org_memberships``, each naming its org, explaining what it can see,
 * showing the seat line when that org provides a plan, and offering a per-org
 * "Leave organization" action (the withdrawal of the consent granted by
 * accepting that org's invite). Renders nothing for solo instructors.
 */

import { useContext, useState } from "react";
import { Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { getErrorMessage } from "@/lib/api";
import { AuthContext } from "@/App";
import { tierDisplayName } from "@/lib/tiers";

function MembershipRow({ membership }) {
  const { setUser } = useContext(AuthContext) || {};
  const [leaving, setLeaving] = useState(false);
  const orgName = membership.org_name || "your organization";
  const seat = membership.seat;

  const leave = async () => {
    const ok = window.confirm(
      `Leave ${orgName}?\n\nThey will no longer see your program students or lesson summaries, ` +
        "and their branding disappears from that program's lessons. " +
        "You keep your account, your other organizations, and all of your data.",
    );
    if (!ok) return;
    setLeaving(true);
    try {
      await api.post("/auth/org-leave", { org_id: membership.org_id });
      // Re-pull /auth/me so this membership drops out and the shell re-renders.
      const me = await api.get("/auth/me");
      setUser?.(me.data);
      toast.success(`You've left ${orgName}`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't leave the organization"));
    } finally {
      setLeaving(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-4 md:p-6"
      data-testid="org-membership-card"
    >
      <div className="flex items-center gap-3">
        <Building2 className="h-5 w-5 shrink-0 text-[var(--text-secondary)]" />
        <div className="min-w-0">
          <p className="font-sans text-[15px] text-[var(--text-primary)]">
            You're part of <span className="font-semibold">{orgName}</span>.
          </p>
          <p className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">
            They can view the students and lesson summaries in their programs, and manage which Hans features you use
            for those programs.
          </p>
          {seat && (
            <p className="mt-1 font-sans text-[13px] font-medium text-[var(--accent-yellow-text)]" data-testid="org-seat-line">
              {orgName} provides your Hans {tierDisplayName(seat.tier)} plan.
            </p>
          )}
        </div>
      </div>
      <button
        type="button"
        disabled={leaving}
        onClick={leave}
        data-testid="org-leave-button"
        className="mt-3 font-sans text-[13px] font-medium text-[var(--state-danger)] hover:underline disabled:opacity-50"
      >
        {leaving ? "Leaving…" : "Leave organization"}
      </button>
    </div>
  );
}

export default function OrgMembershipCard() {
  const { user } = useContext(AuthContext) || {};
  const memberships = user?.org_memberships || [];
  if (!memberships.length) return null;
  return (
    <div className="flex flex-col gap-3">
      {memberships.map((m) => (
        <MembershipRow key={m.org_id} membership={m} />
      ))}
    </div>
  );
}
